import { Injectable } from '@angular/core';
import { HttpClientService } from '../connections/http-client.service';
import { environment } from 'src/environments/environment';
import { AlertService } from '../util/alert.service';
import { Platform } from '@ionic/angular';
import { Browser, Plugins } from '@capacitor/core';
import { Links, Servers } from '../util/utils';
import { TranslateService } from '@ngx-translate/core';
import { AppRate } from '@ionic-native/app-rate/ngx';
import { Subscription } from 'rxjs';
import { Storage } from '@ionic/storage';

const { App } = Plugins;

@Injectable({
    providedIn: 'root'
})
export class AppService {
    private readonly UPGRADE_APP_CODE = 400;

    private appRateText: any;
    private errCodeSub: Subscription;
    private upgradePopupPresent: boolean = false;

    constructor(
        private http: HttpClientService,
        private alert: AlertService,
        private platform: Platform,
        private translate: TranslateService,
        private appRate: AppRate,
        private storage: Storage
    ) {}

    async initMinVersionCheck() {
        this.errCodeSub = this.http.responseCodeObservable.subscribe((code) => {
            if (code === this.UPGRADE_APP_CODE) {
                this.displayUpgradePopup();
            }
        });
    }

    private async displayUpgradePopup() {
        if (!this.upgradePopupPresent) {
            this.upgradePopupPresent = true;
            const { title, text, okButton } = await this.translate.get('utilsMessages.appUpdate').toPromise();
            await this.alert.showCustomDismiss(title, text, okButton);

            if (this.platform.is('ios')) {
                Browser.open({ url: Links.appStore });
            } else if (this.platform.is('android')) {
                Browser.open({ url: Links.playStore });
                App.exitApp();
            }
            this.upgradePopupPresent = false;
        }
    }

    public async firstLaunch(): Promise<boolean> {
        const launchedBefore = await this.storage.get('launchedBefore');
        if (!launchedBefore) {
            this.storage.set('launchedBefore', 'true');
        }
        return !launchedBefore;
    }

    private validateServer(): Promise<any> {
        return this.http.post(
            'ping',
            {},
            (a) => {
                return a;
            },
            { showDefaultAlert: false }
        );
    }

    public async setServer(url: string): Promise<any> {
        url = this.parseAddress(url);
        const WsUrl = this.parseWSAddress(url);

        const prevUrl = environment.baseUrl;
        const prevWsUrl = environment.baseWsUrl;

        environment.baseUrl = url;
        environment.baseWsUrl = WsUrl;
        environment.staging = !Servers.prodUrls.includes(url);

        try {
            await this.validateServer();

            await this.storage.set('environment', JSON.stringify(environment));
        } catch {
            environment.baseUrl = prevUrl;
            environment.baseWsUrl = prevWsUrl;
            environment.staging = !Servers.prodUrls.includes(environment.baseUrl);
            throw Error();
        }
    }

    private parseAddress(url: string) {
        if (url && !url.includes('https://') && !url.includes('http://')) {
            url = 'https://' + url;
        }
        return url;
    }

    private parseWSAddress(url: string) {
        const newUrl = url.replace('https://', 'wss://') + '/ws/';
        return newUrl;
    }

    public async applyStoredServer(): Promise<void> {
        const storedEnv = await this.storage.get('environment');
        if (storedEnv) {
            const env = JSON.parse(storedEnv);
            delete env.version;
            delete env.versionCode;
            Object.assign(environment, env);
        }
    }

    public async rate() {
        this.appRateText = await this.translate.get('appRate').toPromise();
        this.appRate.preferences = {
            usesUntilPrompt: 1,
            inAppReview: true,
            displayAppName: this.appRateText.appTitle,
            storeAppURL: {
                ios: Links.appStore,
                android: Links.playStore
            },
            customLocale: {
                title: this.appRateText.title,
                message: this.appRateText.message,
                rateButtonLabel: this.appRateText.rateButton,
                laterButtonLabel: this.appRateText.rateLaterButton,
                cancelButtonLabel: this.appRateText.cancelButton,
                appRatePromptTitle: this.appRateText.promtTitle,
                yesButtonLabel: this.appRateText.promtYesButton,
                noButtonLabel: this.appRateText.promtNoButton
            }
        };
    }

    public promptForRating() {
        this.appRate.promptForRating(false);
    }

    public sendToStore() {
        this.appRate.navigateToAppStore();
    }
}
